/**
 * DepthGeneratePrompt — the opt-in sheet for generating a section's depth on this device.
 *
 * Nothing heavy happens until the user agrees. The costs are stated BEFORE the button, not
 * discovered afterwards, and the whole run is cancellable — this takes minutes and burns
 * battery, so starting it silently would be taking something without asking.
 *
 * The pipeline itself is imported dynamically. That keeps ONNX Runtime and the depth modules
 * out of the main bundle, so a visitor who never generates anything never downloads them.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { DEPTH_MODEL, DEPTH_MODEL_BYTES, estimateSeconds, formatDuration } from './config'
import type { Progress, Stage } from './generateDepth'

interface Props {
  shopId: string
  sectionId: string
  sectionName?: string
  frameCount: number
  /** (fileName) => absolute URL, SAS included. */
  frameUrl: (name: string) => string
  onClose: () => void
  /** Called once depth exists, so the host can switch to v2 without a reload. */
  onGenerated: () => void
}

const STAGE_LABEL: Record<Stage, string> = {
  preparing: 'Asking the server to start',
  'loading-model': 'Downloading the depth model',
  depth: 'Reading depth from each frame',
  uploading: 'Uploading',
  processing: 'Finishing on the server',
  done: 'Done',
}

export function DepthGeneratePrompt({
  shopId, sectionId, sectionName, frameCount, frameUrl, onClose, onGenerated,
}: Props) {
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState<Progress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Leaving the section mid-run must stop the work, not finish it for a shelf nobody is
  // looking at. Unmount is that signal.
  useEffect(() => () => abortRef.current?.abort(), [])

  const start = useCallback(async () => {
    setRunning(true)
    setError(null)
    const controller = new AbortController()
    abortRef.current = controller
    try {
      // Dynamic: ORT and the pipeline must not be in the main bundle.
      const { generateDepth } = await import('./generateDepth')
      await generateDepth({
        shopId,
        sectionId,
        frameUrl,
        depthModel: DEPTH_MODEL,
        onProgress: setProgress,
        signal: controller.signal,
      })
      onGenerated()
      onClose()
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return   // the user cancelled; say nothing
      setError((err as Error)?.message ?? 'Generation failed.')
      setRunning(false)
    }
  }, [shopId, sectionId, frameUrl, onGenerated, onClose])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    setRunning(false)
    setProgress(null)
  }, [])

  const eta = estimateSeconds(frameCount)
  const pct = progress && progress.total
    ? Math.round((progress.done / progress.total) * 100)
    : 0

  return (
    <div style={backdrop} onClick={running ? undefined : onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <h2 style={title}>
          {running ? 'Building the depth view' : 'Depth view isn’t ready yet'}
        </h2>

        {!running && (
          <>
            <p style={body}>
              {sectionName ? <>“{sectionName}” hasn’t</> : <>This section hasn’t</>} been
              processed for the depth view. Your device can build it — it takes a while, and
              once it’s done everyone who visits gets it instantly.
            </p>
            <dl style={costs}>
              <div style={costRow}>
                <dt style={costKey}>Time</dt>
                <dd style={costValue}>about {formatDuration(eta)} for {frameCount} frames</dd>
              </div>
              <div style={costRow}>
                <dt style={costKey}>Download</dt>
                <dd style={costValue}>
                  {(DEPTH_MODEL_BYTES / 1024 / 1024).toFixed(0)} MB, once per device
                </dd>
              </div>
            </dl>
            <p style={note}>Keep this tab open. You can cancel at any time.</p>
          </>
        )}

        {running && progress && (
          <>
            <p style={body}>{STAGE_LABEL[progress.stage]}</p>
            <div style={track}>
              <div style={{ ...fill, width: `${pct}%` }} />
            </div>
            <p style={note}>
              {progress.total > 1 && `${progress.done} of ${progress.total}`}
              {progress.etaSeconds != null && progress.etaSeconds > 1 &&
                ` · about ${formatDuration(progress.etaSeconds)} left`}
            </p>
          </>
        )}

        {error && <p style={errorText}>{error}</p>}

        <div style={actions}>
          {running ? (
            <button style={secondary} onClick={cancel}>Cancel</button>
          ) : (
            <>
              <button style={secondary} onClick={onClose}>Not now</button>
              <button style={primary} onClick={start}>Build it</button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 900, display: 'grid', placeItems: 'center',
  background: 'rgba(4,6,9,0.72)', backdropFilter: 'blur(6px)', padding: 20,
}
const sheet: React.CSSProperties = {
  width: 'min(420px, 100%)', background: '#0d1117', color: '#e6edf3',
  border: '1px solid #30363d', borderRadius: 16, padding: '22px 22px 18px',
  boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
}
const title: React.CSSProperties = {
  margin: '0 0 10px', fontSize: 19, fontWeight: 650, letterSpacing: '-0.011em',
}
const body: React.CSSProperties = { margin: '0 0 14px', fontSize: 14, lineHeight: 1.55, color: '#c9d1d9' }
const costs: React.CSSProperties = {
  margin: '0 0 14px', padding: '12px 14px', background: 'rgba(255,255,255,0.03)',
  border: '1px solid #21262d', borderRadius: 10, display: 'grid', gap: 8,
}
const costRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 12, margin: 0 }
const costKey: React.CSSProperties = { margin: 0, fontSize: 12, color: '#8b949e' }
const costValue: React.CSSProperties = { margin: 0, fontSize: 13, textAlign: 'right' }
const note: React.CSSProperties = { margin: '0 0 16px', fontSize: 12, color: '#8b949e' }
const errorText: React.CSSProperties = {
  margin: '0 0 14px', fontSize: 13, color: '#f0a4a4',
  background: 'rgba(248,81,73,0.09)', border: '1px solid rgba(248,81,73,0.3)',
  borderRadius: 8, padding: '9px 11px',
}
const track: React.CSSProperties = {
  height: 6, borderRadius: 999, background: '#21262d', overflow: 'hidden', margin: '0 0 10px',
}
const fill: React.CSSProperties = {
  height: '100%', background: '#34d399', borderRadius: 999,
  transition: 'width 0.25s cubic-bezier(.16,1,.3,1)',
}
const actions: React.CSSProperties = { display: 'flex', gap: 8, justifyContent: 'flex-end' }
const baseBtn: React.CSSProperties = {
  border: '1px solid #30363d', borderRadius: 9, padding: '9px 15px',
  fontSize: 14, cursor: 'pointer', background: 'transparent', color: '#e6edf3',
}
const secondary: React.CSSProperties = baseBtn
const primary: React.CSSProperties = {
  ...baseBtn, background: '#34d399', borderColor: '#34d399', color: '#04241a', fontWeight: 650,
}

export default DepthGeneratePrompt
